import React from 'react';
import { useTranslate } from 'react-admin';
import MaterialSymbol from '../../common/MaterialSymbol';
import {
    listingStatusMeta,
    ListingStatusMeta,
    visibilityMeta,
} from '../../../pages/marketplaceVisibility';
import '../marketplace-design.css';

/**
 * Sichtbarkeits-Badge (Marktplatz-Rework T5, Plan 3.4): Öffentlich und
 * Unternehmensgruppe müssen auf einen Blick unterscheidbar sein — deshalb
 * zwei verschiedene Formen, nicht nur zwei Farben: Öffentlich ist ein
 * neutraler Outline-Pill mit Icon `storefront`, Unternehmensgruppe ein
 * gefüllter Pill im Info-Akzent mit Icon `groups`. Das Textlabel steht immer
 * dabei. Pill-Sprache des Marktplatz-Designs (`.mk-pill`), keine Verläufe,
 * kein Kursiv.
 */
export const VisibilityBadge: React.FC<{ visibility: string | null | undefined; short?: boolean; large?: boolean }> = ({
    visibility,
    short = false,
    large = false,
}) => {
    const translate = useTranslate();
    const meta = visibilityMeta(visibility);
    const isGroup = visibility === 'group';

    return (
        <span
            className={`mk-pill ${isGroup ? 'vis-group' : 'vis-public'}${large ? ' vis-lg' : ''}`}
            data-testid={`visibility-badge-${isGroup ? 'group' : 'public'}`}
            data-visibility={isGroup ? 'group' : 'public'}
        >
            <MaterialSymbol name={meta.icon} />
            {translate(short ? meta.shortLabelKey : meta.labelKey)}
        </span>
    );
};

/** Status der Vorlage (ok / warn / err / neu) je Angebotsstatus. */
const TONE_CLASS: Record<ListingStatusMeta['tone'], string> = {
    neutral: 'neu',
    info: 'info',
    success: 'ok',
    warn: 'warn',
    error: 'err',
};

export const ListingStatusChip: React.FC<{ status: string | null | undefined }> = ({ status }) => {
    const translate = useTranslate();
    const meta = listingStatusMeta(status);

    return (
        <span className={`mk-pill ${TONE_CLASS[meta.tone]}`} data-testid={`status-chip-${status ?? 'draft'}`}>
            <MaterialSymbol name={meta.icon} />
            {translate(meta.labelKey)}
        </span>
    );
};

/** Grauer Icon-Badge (Designregel: Icon-Badges sind grau, nie bunt). */
export const IconBadge: React.FC<{ name: string; size?: number }> = ({ name, size = 40 }) => (
    <span className="mk-ic" style={{ width: size, height: size }}>
        <MaterialSymbol name={name} size={Math.round(size * 0.55)} />
    </span>
);
